import { cartAtom, itemsAtom, optionGroupsAtom } from "@/atoms"
import { useBrowsingChannelCatalog } from "@/hooks/use-browsing-channel-catalog"
import { showToast } from "@/components/feedback"
import { Gift } from "@/icons"
import type { CartItem } from "@/types"
import type { EntitlementGrant } from "@/types/loyalty-benefits"
import { formatBRL } from "@/lib/format-brl"
import { openEntitlements } from "@/lib/loyalty-benefits"
import {
  buildFreeProductCartItem,
  findClaimedFreeProductLine,
  refMatchesCartLine,
} from "@/lib/free-product-cart-item"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { v4 as uuid } from "uuid"
import { useAtom, useAtomValue } from "jotai"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useCheckoutContext } from "../CheckoutContext"
import { StepFooter } from "../components/StepFooter"
import { StepHeader } from "../components/StepHeader"
import { useEnforceWizardEntry } from "../hooks/useEnforceWizardEntry"
import { checkoutAtom } from "../state"
import { benefitClaimInstants } from "../lib/checkout-pricing"
import { CheckoutCouponSection } from "../components/coupons/CheckoutCouponSection"
import { LoyaltyPointsSection } from "../components/LoyaltyPointsSection"
import { RewardsSection } from "../components/rewards/RewardsSection"
import { Icon, PageContainer, SectionHeader, SelectableCard } from "@/components/ui"

/** Headline of an unlocked benefit. */
function entitlementLabel(grant: EntitlementGrant): string {
  const { benefit } = grant
  if (benefit.freeProduct) return `${benefit.freeProduct.item.name || "Produto"} grátis`
  if (benefit.orderDiscount)
    return `${formatBRL(benefit.orderDiscount.amount.value)} de desconto`
  if (benefit.shipping?.type === "FREE_SHIPPING") return "Frete grátis"
  return `${formatBRL(benefit.shipping?.amount?.value ?? 0)} no frete`
}

/**
 * What distinguishes two grants of the same reward. A customer who completed
 * the recurrence four times holds four identical labels, so without this the
 * list reads as a duplication bug.
 */
function entitlementDetail(grant: EntitlementGrant): string | undefined {
  const parts: string[] = []
  if (grant.unlockedAt) {
    parts.push(`Desbloqueado em ${new Date(grant.unlockedAt).toLocaleDateString("pt-BR")}`)
  }
  if (grant.expiresAt) {
    parts.push(`vale até ${new Date(grant.expiresAt).toLocaleDateString("pt-BR")}`)
  }
  return parts.length ? parts.join(", ") : undefined
}

/** The claimed-gift bookkeeping without the given grants' entries. */
function forgetClaimedLines(
  claimed: Record<string, string> | undefined,
  grantIds: string[],
): Record<string, string> {
  const next = { ...(claimed ?? {}) }
  for (const grantId of grantIds) delete next[grantId]
  return next
}

export function ExtrasStep() {
  const { addresses, hasExtras } = useCheckoutContext()
  const cart = useAtomValue(cartAtom)
  const items = useAtomValue(itemsAtom)
  const { itemOffers, categories } = useBrowsingChannelCatalog()
  const optionGroups = useAtomValue(optionGroupsAtom)
  const { addItem, removeItem } = useOptimisticCart()
  const [state, setState] = useAtom(checkoutAtom)
  const navigate = useNavigate()

  useEnforceWizardEntry("extras", addresses)

  useEffect(() => {
    if (!hasExtras) {
      navigate("/cart/checkout/review", { replace: true })
    }
  }, [hasExtras, navigate])

  const couponApplied = state.couponCodes.length > 0
  const loyaltyActive = state.loyalty.use
  const claimAt = benefitClaimInstants(state)
  const redeemableGrants = openEntitlements(state.benefits.active, state.benefits.available, claimAt)

  /**
   * The merchant caps the order at one benefit. buildLoyaltyDiscounts already
   * silently keeps only the best one, so letting the customer tick several was
   * a promise the checkout never kept — the extra picks vanished at submit.
   */
  const singleBenefitOnly =
    state.benefits.active?.stackingPolicy === "SINGLE_BENEFIT_PER_ORDER"

  /**
   * Picking a free-product reward puts the item in the bag.
   *
   * Open Delivery has no free-item concept: the line is billed at full price
   * and the reward rides in discounts[] targeting it. Without the line there
   * is nothing to target, and the claim used to be dropped in silence at
   * submit while the card stayed ticked.
   *
   * Returns the id of the line this checkout added, so dropping the reward can
   * take that exact line back out — and only it. `undefined` means the bag
   * already had the product: it is the customer's line and stays either way.
   */
  function claimFreeProductLine(
    grant: EntitlementGrant,
  ): { ok: true; lineId?: string } | { ok: false } {
    const ref = grant.benefit.freeProduct?.item
    if (!ref) return { ok: true }

    if (cart.items.some((line) => refMatchesCartLine(ref, line))) return { ok: true }

    const resolved = buildFreeProductCartItem(
      ref,
      { items, itemOffers, optionGroups, categories },
      uuid(),
    )
    if (!resolved.ok) {
      showToast.error(
        resolved.reason === "requires-options"
          ? "Esse brinde tem opções a escolher. Adicione ele pelo cardápio para usar o benefício."
          : "Não conseguimos adicionar o brinde à sacola agora. Tente de novo em instantes.",
      )
      return { ok: false }
    }

    return { ok: true, lineId: addItem(resolved.cartItem) }
  }

  /** The bag line this checkout is holding for each of the given grants. */
  function claimedLinesOf(grantIds: string[]): CartItem[] {
    const claimed = state.benefits.freeProductLines ?? {}
    return grantIds.flatMap((grantId) => {
      const ref = state.benefits.available.find((g) => g.grantId === grantId)?.benefit
        .freeProduct?.item
      if (!ref) return []
      const line = findClaimedFreeProductLine(cart.items, ref, claimed[grantId])
      return line ? [line] : []
    })
  }

  /**
   * Dropping a reward hands its gift line back, and picking another under the
   * one-benefit-per-order policy hands back the line of the pick it replaces —
   * leaving it behind billed the customer for a brinde they no longer get.
   *
   * The same reward held twice (the recurrence unlocked it again) transfers its
   * line instead of churning the bag: taking it out and putting it back would
   * leave the new claim with nothing to target while the removal is still only
   * optimistic.
   */
  function toggleBenefit(grantId: string) {
    const grant = state.benefits.available.find((g) => g.grantId === grantId)
    if (!grant) return

    if (state.benefits.selectedIds.includes(grantId)) {
      for (const line of claimedLinesOf([grantId])) removeItem(line.id)
      setState((prev) => ({
        ...prev,
        benefits: {
          ...prev.benefits,
          selectedIds: prev.benefits.selectedIds.filter((id) => id !== grantId),
          freeProductLines: forgetClaimedLines(prev.benefits.freeProductLines, [grantId]),
        },
      }))
      return
    }

    const replaced = singleBenefitOnly
      ? state.benefits.selectedIds.filter((id) => id !== grantId)
      : []
    const releasable = claimedLinesOf(replaced)

    const ref = grant.benefit.freeProduct?.item
    const inherited = ref ? releasable.find((line) => refMatchesCartLine(ref, line)) : undefined

    const claim = inherited
      ? { ok: true as const, lineId: inherited.id }
      : claimFreeProductLine(grant)
    if (!claim.ok) return

    for (const line of releasable) {
      if (line.id !== inherited?.id) removeItem(line.id)
    }

    setState((prev) => {
      const selectedIds = singleBenefitOnly
        ? [grantId]
        : [...prev.benefits.selectedIds.filter((id) => id !== grantId), grantId]
      const freeProductLines = forgetClaimedLines(prev.benefits.freeProductLines, replaced)
      if (claim.lineId) freeProductLines[grantId] = claim.lineId
      return { ...prev, benefits: { ...prev.benefits, selectedIds, freeProductLines } }
    })
  }

  return (
    <>
      <StepHeader title="Cupom e pontos" description="Opcional. Se não tiver, é só continuar." />

      <PageContainer as="section" className="space-y-6">
        <CheckoutCouponSection
          pointsInUse={loyaltyActive}
          onUseCouponInstead={() => setState((prev) => ({ ...prev, loyalty: { ...prev.loyalty, use: false } }))}
        />

        <LoyaltyPointsSection />

        <RewardsSection />

        {redeemableGrants.length > 0 && (
          <div>
            <SectionHeader
              title="Benefícios desbloqueados"
              description={
                singleBenefitOnly
                  ? "Esta loja aplica um benefício por pedido. Escolha qual usar."
                  : "Escolha quais aplicar neste pedido."
              }
            />
            <div
              className="flex flex-col gap-2"
              role={singleBenefitOnly ? "radiogroup" : "group"}
              aria-label="Benefícios desbloqueados"
            >
              {redeemableGrants.map((grant) => (
                <SelectableCard
                  key={grant.grantId}
                  selected={state.benefits.selectedIds.includes(grant.grantId)}
                  indicator={singleBenefitOnly ? "radio" : "checkbox"}
                  inGroup={singleBenefitOnly}
                  tone="secondary"
                  icon={<Icon as={Gift} size="xl" tone="secondary" weight="duotone" />}
                  title={entitlementLabel(grant)}
                  subtitle={entitlementDetail(grant)}
                  onSelect={() => toggleBenefit(grant.grantId)}
                />
              ))}
            </div>
          </div>
        )}
      </PageContainer>

      <StepFooter
        onBack={() => navigate("/cart/checkout/payment", { replace: true })}
        onContinue={() => navigate("/cart/checkout/review")}
        continueLabel={!couponApplied && !state.loyalty.use ? "Pular esta etapa" : "Continuar"}
      />
    </>
  )
}
